import { Col, FlexboxGrid, FormInstance, Row, Schema, Text } from 'rsuite';
import PersonDetails, { PersonDetailsProps } from '../../PersonDetails';
import React, { forwardRef, useMemo } from 'react';
import { formValuePersonSchema } from '../utils';
import { FormModelPerson } from 'src/types/person';
import {
  emailRequiredValidation,
  uniqueEmailValidation,
} from 'src/common/validation';

const { ObjectType } = Schema.Types;

interface SingleOwnersFormProps
  extends Pick<
    PersonDetailsProps,
    'disabled' | 'disabledFields' | 'formValues'
  > {
  disabledFields?: Partial<Record<keyof FormModelPerson, boolean>>;
  existingEmails?: string[];
  hiddenFields?: Partial<Record<keyof FormModelPerson, boolean>>;
  onChange: (value: FormModelPerson) => void;
  onRemove?: () => void;
  propertyAddress: string;
  showRemove?: boolean;
  title: string;
}

export const SingleOwnersForm = forwardRef<FormInstance, SingleOwnersFormProps>(
  (
    {
      existingEmails,
      hiddenFields,
      onChange,
      onRemove,
      showRemove = false,
      title,
      ...rest
    },
    ref
  ) => {
    const formModel = useMemo(() => {
      return Schema.Model<FormModelPerson>({
        ...formValuePersonSchema,
        email: existingEmails?.length
          ? uniqueEmailValidation(
              existingEmails,
              'Email address is already used by another owner'
            )
          : emailRequiredValidation,
        ...(!hiddenFields?.physicalAddress && {
          completePhysicalAddress: ObjectType().isRequired(
            'Select a physical address from the dropdown to continue'
          ),
        }),
      });
    }, [existingEmails, hiddenFields?.physicalAddress]);

    return (
      <>
        <Row className="mt-8">
          <Col
            xs={24}
            md={12}
          >
            <FlexboxGrid justify="space-between">
              <Text className="text-bold">{title}</Text>
              {showRemove && (
                <a
                  href="#"
                  onClick={onRemove}
                >
                  Remove owner
                </a>
              )}
            </FlexboxGrid>
          </Col>
        </Row>
        <Row>
          <Col
            style={{ marginTop: '12px' }}
            xs={24}
            md={12}
          >
            <PersonDetails
              {...rest}
              formModel={formModel}
              hiddenFields={hiddenFields}
              optionalFields={{
                companyName: 'optional',
              }}
              ref={ref}
              setFormValues={onChange}
              validationModel={formModel}
            />
          </Col>
        </Row>
      </>
    );
  }
);

SingleOwnersForm.displayName = 'SingleOwnersForm';
